import { ConvexError } from "convex/values";
import {
  createAppErrorData,
  ERROR_CODES,
  type AppErrorData,
} from "@shared/errors/app-error";

type ThrowErrorOptions = Partial<Omit<AppErrorData, "message">>;

export function throwError(message: string, options?: ThrowErrorOptions): never;
export function throwError(data: AppErrorData): never;
export function throwError(
  messageOrData: string | AppErrorData,
  options: ThrowErrorOptions = {},
): never {
  const errorData =
    typeof messageOrData === "string"
      ? createAppErrorData(messageOrData, options)
      : messageOrData;

  throw new ConvexError({
    code: errorData.code,
    message: errorData.message,
    ...(errorData.redirectTo !== undefined && {
      redirectTo: errorData.redirectTo,
    }),
    ...(errorData.deltaCents !== undefined && {
      deltaCents: errorData.deltaCents,
    }),
    ...(errorData.settlement !== undefined && {
      settlement: errorData.settlement,
    }),
    ...(errorData.suggestion !== undefined && {
      suggestion: errorData.suggestion,
    }),
  });
}

export function throwAuthError(message: string): never {
  throwError(message, { code: ERROR_CODES.AUTH_REQUIRED });
}

export function throwPermissionError(message: string): never {
  throwError(message, { code: ERROR_CODES.FORBIDDEN });
}

export function throwValidationError(message: string): never {
  throwError(message, { code: ERROR_CODES.VALIDATION_FAILED });
}

export function validationErrorMessage(error: unknown): string | null {
  if (!(error instanceof ConvexError)) return null;
  const data: unknown = error.data;
  if (typeof data !== "object" || data === null) return null;
  const isValidation =
    Reflect.get(data, "kind") === "validation" ||
    Reflect.get(data, "code") === ERROR_CODES.VALIDATION_FAILED;
  if (!isValidation) return null;
  const message: unknown = Reflect.get(data, "message");
  return typeof message === "string" ? message : "Validation error";
}

export function throwNotFoundError(message: string): never {
  throwError(message, { code: ERROR_CODES.NOT_FOUND });
}

export function throwConflictError(message: string): never {
  throwError(message, { code: ERROR_CODES.CONFLICT });
}

export function throwInvalidStateError(message: string): never {
  throwError(message, { code: ERROR_CODES.INVALID_STATE });
}

type SettlementPrompt = { deltaCents?: number; settlement?: string };

export function throwConfirmationRequiredError(
  message: string,
  prompt: SettlementPrompt = {},
): never {
  throwError(message, { code: ERROR_CODES.CONFIRMATION_REQUIRED, ...prompt });
}

export function throwSettlementChoiceRequiredError(
  message: string,
  prompt: SettlementPrompt,
): never {
  throwError(message, { code: ERROR_CODES.SETTLEMENT_CHOICE_REQUIRED, ...prompt });
}

export function throwDuplicateError(message: string): never {
  throwError(message, { code: ERROR_CODES.DUPLICATE });
}

export function throwExpiredError(message: string): never {
  throwError(message, { code: ERROR_CODES.EXPIRED });
}

export function throwRateLimitError(message: string): never {
  throwError(message, { code: ERROR_CODES.RATE_LIMIT });
}

export function throwQuotaExceededError(message: string): never {
  throwError(message, { code: ERROR_CODES.QUOTA_EXCEEDED });
}

export function throwDependencyError(message: string): never {
  throwError(message, { code: ERROR_CODES.DEPENDENCY_FAILED });
}

export function appErrorCode(error: unknown): string | null {
  if (!(error instanceof ConvexError)) return null;
  const data: unknown = error.data;
  const code: unknown = typeof data === "object" && data !== null ? Reflect.get(data, "code") : undefined;
  return typeof code === "string" ? code : null;
}

export function appErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ConvexError) {
    const data: unknown = error.data;
    if (typeof data === "string") return data;
    const message: unknown = typeof data === "object" && data !== null ? Reflect.get(data, "message") : undefined;
    if (typeof message === "string") return message;
  }
  return fallback;
}
